import type { LucideIcon } from 'lucide-react';
import {
  FileCheck2,
  Gauge,
  LayoutDashboard,
  Leaf,
  Sparkles,
  ScrollText,
  Shield,
  Target,
  Users,
} from 'lucide-react';

export type EsgSectionId =
  | 'overview'
  | 'onboarding'
  | 'double-materiality'
  | 'dashboard'
  | 'esrs-2-general'
  | 'environmental'
  | 'social'
  | 'governance'
  | 'policies-evidence'
  | 'assurance-report-builder';

export interface EsgMetric {
  label: string;
  value: string;
  detail: string;
}

export interface EsgSectionDefinition {
  id: EsgSectionId;
  to: string;
  label: string;
  title: string;
  description: string;
  status: string;
  owner: string;
  reportCycle: string;
  completion: number;
  icon: LucideIcon;
  metrics: EsgMetric[];
  priorities: string[];
  deliverables: string[];
  showInNavigation?: boolean;
}

export const ESG_SECTIONS: EsgSectionDefinition[] = [
  {
    id: 'overview',
    to: '/esg/overview',
    label: 'Overview',
    title: 'CSRD Workspace Overview',
    description:
      'Structured entry point for the CSRD module with reporting scope, double materiality, ESRS coverage, and execution flow.',
    status: 'Program setup in progress',
    owner: 'Sustainability Office',
    reportCycle: 'FY2026 reporting cycle',
    completion: 34,
    icon: Gauge,
    metrics: [
      {
        label: 'ESRS datapoints',
        value: '1,185',
        detail: 'EFRAG IG 3 datapoints mapped into the module blueprint',
      },
      {
        label: 'Cross-cutting',
        value: '191',
        detail: 'ESRS 2 and MDR datapoints that affect all reporting tracks',
      },
      {
        label: 'Environmental',
        value: '532',
        detail: 'E1-E5 datapoints after workbook normalization',
      },
      {
        label: 'Social + Gov',
        value: '462',
        detail: 'S1-S4 plus G1 datapoints across people and conduct',
      },
    ],
    priorities: [
      'Establish the reporting perimeter before topic-level data collection starts.',
      'Treat double materiality as the gating workflow for all downstream disclosure work.',
      'Separate cross-cutting ESRS 2 content from topical E, S, and G data collection.',
    ],
    deliverables: [
      'CSRD module charter with governance, timing, and owner structure.',
      'Initial reporting universe derived from the ESRS datapoint workbook.',
      'Visible decision trail from scope definition to active disclosures.',
    ],
  },
  {
    id: 'dashboard',
    to: '/esg/dashboard',
    label: 'Dashboard',
    title: 'CSRD Dashboard',
    description:
      'Executive control tower for disclosure progress, evidence readiness, material topic coverage, and assurance blockers.',
    status: 'Live monitoring',
    owner: 'PMO and ESG team',
    reportCycle: 'Monthly refresh',
    completion: 39,
    icon: LayoutDashboard,
    metrics: [
      {
        label: 'Active disclosures',
        value: '84',
        detail: 'Disclosures currently in scope after materiality screening',
      },
      {
        label: 'Conditional items',
        value: '269',
        detail: 'Datapoints that depend on topic relevance or specific scenarios',
      },
      {
        label: 'Phasing-in',
        value: '552',
        detail: 'Datapoints affected by ESRS transition provisions',
      },
      {
        label: 'Open blockers',
        value: '17',
        detail: 'Missing evidence, owners, or unresolved methodology dependencies',
      },
    ],
    priorities: [
      'Monitor only the active disclosure universe rather than the full raw datapoint inventory.',
      'Keep assurance blockers visible early, especially for narrative-heavy topics.',
      'Show standard-level readiness so owners know where the critical path sits.',
    ],
    deliverables: [
      'Management scorecard for cross-cutting and topical standards.',
      'Exception queue for missing evidence and unresolved scope questions.',
      'Roll-up of disclosure, evidence, and review readiness.',
    ],
  },
  {
    id: 'onboarding',
    to: '/esg/onboarding',
    label: 'Onboarding',
    title: 'CSRD Onboarding',
    description:
      'Collect the starter profile for CSRD reporting through onboarding questions, AI guidance, and auto-filled baseline fields.',
    status: 'Starter intake',
    owner: 'ESG PMO and Reporting Lead',
    reportCycle: 'At setup and major scope changes',
    completion: 21,
    icon: Sparkles,
    metrics: [
      {
        label: 'Starter questions',
        value: '9',
        detail: 'Question themes derived from ESRS 2, E1, S1, and G1 workbook structure',
      },
      {
        label: 'Core fields',
        value: '14',
        detail: 'Basic onboarding fields for perimeter, governance, policies, and evidence',
      },
      {
        label: 'Cross-cutting anchors',
        value: 'BP + GOV + SBM + IRO',
        detail: 'ESRS 2 areas used to frame the onboarding baseline',
      },
      {
        label: 'Topical signals',
        value: 'E + S + G',
        detail: 'Starter indicators for climate, workforce, and business conduct readiness',
      },
    ],
    priorities: [
      'Capture reporting perimeter and employee threshold early because they affect phased-in logic.',
      'Use onboarding to establish governance, value-chain coverage, and core document readiness before deeper workflows start.',
      'Treat climate, workforce, and conduct policy questions as starter signals for E, S, and G activation.',
    ],
    deliverables: [
      'Starter company profile for CSRD reporting.',
      'Initial view of reporting boundary, governance, and value-chain readiness.',
      'Seeded baseline fields that reduce repetitive data entry in later workspaces.',
    ],
  },
  {
    id: 'double-materiality',
    to: '/esg/double-materiality',
    label: 'Double Materiality',
    title: 'Reporting Scope & Double Materiality',
    description:
      'Define methodology, review IROs, decide material topics, and lock the active ESRS scope for the reporting year.',
    status: 'Critical path',
    owner: 'ESG Program Lead and Finance',
    reportCycle: 'Annual with interim refreshes',
    completion: 48,
    icon: Target,
    metrics: [
      {
        label: 'Topics assessed',
        value: '23',
        detail: 'Candidate sustainability matters under review',
      },
      {
        label: 'Material topics',
        value: '11',
        detail: 'Topics proposed as active for the draft reporting perimeter',
      },
      {
        label: 'IRO items',
        value: '38',
        detail: 'Impacts, risks, and opportunities captured in the working register',
      },
      {
        label: 'Pending sign-off',
        value: '2',
        detail: 'Management approvals still required before locking scope',
      },
    ],
    priorities: [
      'Keep impact and financial materiality visible as distinct but connected lenses.',
      'Record the rationale for every topic decision so downstream disclosure status is defensible.',
      'Freeze the active reporting scope before detailed E, S, and G collection accelerates.',
    ],
    deliverables: [
      'Approved methodology with thresholds and stakeholder coverage.',
      'IRO register linked to ESRS topics and evidence sources.',
      'Locked list of material topics that activates the disclosure workspaces.',
    ],
  },
  {
    id: 'policies-evidence',
    to: '/esg/double-materiality/datapoints-policies',
    label: 'Gap analysis',
    title: 'Gap analysis',
    description:
      'Gap analysis workspace showing which datapoints are reported and which policies and documents still need to be linked.',
    status: 'Document intake',
    owner: 'Policy Office and Data Owners',
    reportCycle: 'Continuous maintenance',
    completion: 35,
    icon: ScrollText,
    metrics: [
      {
        label: 'Linked packs',
        value: '61',
        detail: 'Documents proposed as reusable evidence across CSRD sections',
      },
      {
        label: 'AI-ready',
        value: '37',
        detail: 'Files prepared for extraction and suggestion workflows',
      },
      {
        label: 'Needs refresh',
        value: '12',
        detail: 'Evidence nearing expiry or requiring updated period coverage',
      },
      {
        label: 'Missing anchors',
        value: '9',
        detail: 'Critical datapoint groups still without named source files',
      },
    ],
    priorities: [
      'Keep one document model for ESRS 2, environmental, social, and governance areas.',
      'Make evidence traceability visible at datapoint and disclosure level.',
      'Support AI-assisted drafting without obscuring the human approval step.',
    ],
    deliverables: [
      'Reusable evidence library linked to topics and disclosures.',
      'Document freshness and approval indicators.',
      'Grounding layer for AI suggestions and assurance requests.',
    ],
  },
  {
    id: 'esrs-2-general',
    to: '/esg/esrs-2-general',
    label: 'ESRS 2 General',
    title: 'ESRS 2 General Disclosures',
    description:
      'Cross-cutting disclosures covering basis of preparation, governance, strategy, impacts, risks, opportunities, and materiality foundations.',
    status: 'Foundation drafting',
    owner: 'Corporate Secretariat and Sustainability Office',
    reportCycle: 'Annual core narrative',
    completion: 31,
    icon: ScrollText,
    metrics: [
      {
        label: 'Datapoints',
        value: '146',
        detail: 'General disclosure datapoints in the workbook',
      },
      {
        label: 'Conditional',
        value: '52',
        detail: 'Items shown only when the relevant condition is met',
      },
      {
        label: 'Narrative',
        value: '101',
        detail: 'Predominantly narrative responses requiring strong evidence linking',
      },
      {
        label: 'Priority DRs',
        value: '12',
        detail: 'Distinct disclosure requirements across BP, GOV, SBM, and IRO',
      },
    ],
    priorities: [
      'Build the cross-cutting narrative before topical disclosure drafting branches out.',
      'Keep links between materiality conclusions and ESRS 2 explanations explicit.',
      'Use this section as the bridge between governance, strategy, and topical disclosures.',
    ],
    deliverables: [
      'Basis of preparation and reporting boundary narrative.',
      'Governance and strategy disclosures aligned to management sign-off.',
      'Cross-cutting IRO and materiality explanations for the sustainability statement.',
    ],
  },
  {
    id: 'environmental',
    to: '/esg/environmental',
    label: 'Environmental',
    title: 'Environmental Standards',
    description:
      'Manage E1-E5 climate, pollution, water, biodiversity, and circular economy disclosures through one environmental control layer.',
    status: 'Topic activation',
    owner: 'Environment and Operations',
    reportCycle: 'Quarterly evidence refresh',
    completion: 28,
    icon: Leaf,
    metrics: [
      {
        label: 'Standards',
        value: '5',
        detail: 'E1 through E5 workspaces grouped under one environmental track',
      },
      {
        label: 'Datapoints',
        value: '532',
        detail: 'Environmental datapoints across climate and resource topics',
      },
      {
        label: 'Largest topic',
        value: 'E1 · 217',
        detail: 'Climate change dominates the environmental workload',
      },
      {
        label: 'Phase-in heavy',
        value: '195',
        detail: 'E1 and E4 introduce large transition-period disclosure volume',
      },
    ],
    priorities: [
      'Let double materiality decide which environmental standards are active by default.',
      'Separate climate-heavy quantitative work from narrative-heavy nature and policy disclosures.',
      'Keep methodology notes visible for estimated or model-based environmental metrics.',
    ],
    deliverables: [
      'Standard-level views for E1, E2, E3, E4, and E5.',
      'Evidence-linked datapoint capture with conditional and phase-in awareness.',
      'Environmental readiness view for management and assurance teams.',
    ],
  },
  {
    id: 'social',
    to: '/esg/social',
    label: 'Social',
    title: 'Social Standards',
    description:
      'Coordinate S1-S4 workforce, value chain workers, affected communities, and consumers/end-users disclosures.',
    status: 'Cross-functional collection',
    owner: 'HR, Procurement, Legal',
    reportCycle: 'Quarterly sync',
    completion: 26,
    icon: Users,
    metrics: [
      { label: 'Standards', value: '4', detail: 'S1 through S4 grouped into one social track' },
      {
        label: 'Datapoints',
        value: '407',
        detail: 'Social datapoints dominated by narrative disclosures',
      },
      {
        label: 'Largest topic',
        value: 'S1 · 198',
        detail: 'Own workforce is the heaviest single social standard',
      },
      {
        label: 'Narrative mix',
        value: '248',
        detail: 'Narrative datapoints across social standards',
      },
    ],
    priorities: [
      'Break social content into internal workforce topics versus external stakeholder topics.',
      'Support evidence and narrative collection from multiple business owners.',
      'Preserve the logic behind vulnerable-group and geography-level conclusions.',
    ],
    deliverables: [
      'Social topic workspaces for S1, S2, S3, and S4.',
      'Rationale trail for stakeholder and impact scoping decisions.',
      'Data and narrative packs suitable for assurance review.',
    ],
  },
  {
    id: 'governance',
    to: '/esg/governance',
    label: 'Governance',
    title: 'Governance Standards',
    description:
      'Collect G1 business conduct disclosures and connect them to governance, compliance, and ethics evidence.',
    status: 'Owner alignment',
    owner: 'Legal and Compliance',
    reportCycle: 'Annual update',
    completion: 33,
    icon: Shield,
    metrics: [
      { label: 'Datapoints', value: '55', detail: 'G1 disclosure inventory in the workbook' },
      {
        label: 'Narrative',
        value: '28',
        detail: 'Narrative-heavy conduct and policy explanations',
      },
      {
        label: 'Conditional',
        value: '10',
        detail: 'Scenario-based or policy-dependent datapoints',
      },
      {
        label: 'Priority DRs',
        value: '8',
        detail: 'Governance, policy, actions, and incidents coverage',
      },
    ],
    priorities: [
      'Keep governance content tightly linked to existing policies and conduct controls.',
      'Use one workspace for anti-corruption, whistleblowing, and business conduct narratives.',
      'Prepare governance disclosures early because they cut across several assurance questions.',
    ],
    deliverables: [
      'G1 disclosure pack with linked policy and incident evidence.',
      'Owner-approved conduct narrative and KPI references.',
      'Review-ready governance section for the report builder.',
    ],
  },
  {
    id: 'assurance-report-builder',
    to: '/esg/assurance-report-builder',
    label: 'Assurance & Report',
    title: 'Assurance & Report Builder',
    description:
      'Package approved disclosures into a sustainability statement draft and surface assurance-readiness controls and review trails.',
    status: 'Final assembly',
    owner: 'Reporting Lead and Assurance Coordinator',
    reportCycle: 'Draft and publication milestones',
    completion: 22,
    icon: FileCheck2,
    metrics: [
      {
        label: 'Approved sections',
        value: '7',
        detail: 'Sections currently ready for inclusion in the draft statement',
      },
      {
        label: 'Assurance requests',
        value: '14',
        detail: 'Expected evidence requests from internal or external reviewers',
      },
      { label: 'Review gates', value: '4', detail: 'Formal checkpoints before publication' },
      {
        label: 'Open comments',
        value: '19',
        detail: 'Pending review feedback and unresolved editorial items',
      },
    ],
    priorities: [
      'Keep assurance readiness visible before report generation becomes a formatting exercise.',
      'Generate the report from approved workflow state rather than ad hoc text assembly.',
      'Maintain an auditable trail from materiality decisions to published disclosures.',
    ],
    deliverables: [
      'CSRD sustainability statement draft for the active reporting year.',
      'Assurance readiness summary with evidence coverage and open requests.',
      'Versioned report package for review and publication.',
    ],
  },
];

export const ESG_NAV_SECTIONS = ESG_SECTIONS.filter(
  (section) => section.showInNavigation !== false
);

export function isEsgSection(sectionId: string): sectionId is EsgSectionId {
  return ESG_SECTIONS.some((section) => section.id === sectionId);
}

export function getEsgSection(sectionId: EsgSectionId): EsgSectionDefinition {
  return ESG_SECTIONS.find((section) => section.id === sectionId) ?? ESG_SECTIONS[0];
}

export function getNextEsgSection(sectionId: EsgSectionId): EsgSectionDefinition | null {
  const currentIndex = ESG_SECTIONS.findIndex((section) => section.id === sectionId);
  if (currentIndex < 0 || currentIndex === ESG_SECTIONS.length - 1) {
    return null;
  }
  return ESG_SECTIONS[currentIndex + 1] ?? null;
}
